import { MenuItemText, MenuPresentationProps } from "@eisinc/MobiusOverrides/Menu";
import { DEFAULT_MENU_WIDTH, MAX_DEFAULT_MENU_WIDTH } from "@eisinc/XDL/constants";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xBaseTheme } from "@eisinc/XDL/types";
import { color, styledScrollbar } from "@eisinc/XDL/Utilities";
import { ellipsis } from "@eisinc/XDL/Utilities/string";
import xScreen from "@eisinc/XDL/Utilities/xScreen";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import MenuDefaultProps from "@insite/mobius/Menu/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "text.link",
    },
    active: {
        background: "common.ui2",
        text: "text.main",
        icon: "common.link",
    },
};

export default {
    defaultProps: {
        ...MenuDefaultProps,
        mergeCss: true,
        cssOverrides: {
            menu: css`
                background: ${color("common.white")};
                width: ${getProp("width", DEFAULT_MENU_WIDTH)}px;
                max-width: ${MAX_DEFAULT_MENU_WIDTH}vw;
                z-index: ${getProp("theme.zIndex.menu")};
                ${({ maxNumberOfLinks }: any) => {
                    if (maxNumberOfLinks) {
                        const linkHeight = 35;
                        const maxLinksHeight = maxNumberOfLinks * linkHeight + 50;

                        return css`
                            height: ${maxLinksHeight}px;
                        `;
                    }

                    return css`
                        height: 100%;
                    `;
                }}
                max-height: calc(calc(100vh - 200px) * 0.8);
                list-style: none;
                margin: 0;
                overflow: auto;
                box-shadow: 1px 0 0 0 ${color("text.main")};
                outline: 1px solid ${color("common.border")};
                &&& {
                    padding: 0;
                }
                ${xScreen("lte", 1165)} {
                    max-width: 26vw;
                }
                &:has(div > [aria-expanded="true"]) {
                    border-bottom-right-radius: 0;
                    & > div > [aria-expanded="true"] + [data-tippy-root] .MenuStyle {
                        border-bottom-left-radius: 0 !important;
                    }
                }
                ${styledScrollbar(4, "", 0)}
                display: block;
                position: static;
            `,
            menuItem: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0;
                height: auto;
                background-color: ${color("common.white")};
                & + [data-tippy-root] {
                    &,
                    & > div {
                        height: 100%;
                    }
                }

                &:focus {
                    outline: none;
                }

                ${StyledButton} {
                    width: 100%;
                    display: flex;
                    justify-content: flex-start;
                    gap: 8px;
                    align-items: center;
                    padding: 9px 0px 9px 12px;
                    min-height: unset;
                }

                ${TypographyStyle} {
                    color: ${color("common.ui6")};
                    font-size: ${fontConfig.singleLineSmall.size}px;
                    line-height: ${fontConfig.singleLineSmall.lineHeight};
                }

                ${IconWrapper} {
                    color: ${color("common.ui6")};
                    width: 14px;
                    height: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    padding: 0;
                    right: 8px;
                }

                &:focus,
                &:focus-within,
                &:hover,
                &[aria-expanded="true"] {
                    background: ${color(menuItemColorStyle.active.background)};
                    ${IconWrapper} {
                        color: ${color("common.ui6")};
                    }
                    ${MenuItemText} {
                        color: ${color("common.ui6")};
                    }
                }
            `,
            wrapper: css`
                &[aria-expanded="true"] {
                    .trigger-item {
                        background-color: ${color("common.white")};

                        ${IconWrapper} {
                            rotate: 180deg;
                            color: ${color("text.main")};
                        }

                        ${TypographyStyle} {
                            color: ${color("text.main")};
                        }
                    }
                }
            `,
            parentMenuLink: css`
                color: ${color("common.ui6")};
                font-size: ${fontConfig.singleLineRegular.size}px;
                line-height: ${fontConfig.singleLineRegular.lineHeight};
                font-weight: ${fontConfig.weight.medium};
                width: 100%;
                padding: 12px;
                display: flex;
                flex-direction: column;
                align-items: start;
                gap: 4px;

                & > div {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                ${IconWrapper} {
                    display: none;
                }

                &::after {
                    content: "";
                    width: 56px;
                    height: 3px;
                    background-color: ${color("primary")};
                }

                &:hover {
                    color: ${color("primary")};

                    ${IconWrapper} {
                        display: block;
                        color: ${color("primary")};
                    }
                }
            `,
        },
        menuItemTypographyProps: {
            css: css`
                font-size: ${fontConfig.singleLineSmall.size}px;
                line-height: ${fontConfig.singleLineSmall.lineHeight};
                font-weight: ${({ weight }: any) => weight};
                letter-spacing: 0;
                ${ellipsis(2)}
                &&& {
                    display: inline;
                    padding: 0;
                    width: auto;
                }
            `,
        },
    } as MenuPresentationProps,
} as xBaseTheme["menu"];
